import './apply-form.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { useEffect, useRef, useState } from 'react';
import { IApplyFormProps } from './interfaces/IApplyFormProps';
import { IApplyFormState } from './interfaces/IApplyFormState';
import { FormValidationService } from '../../../services/formValidationService';
import { Toast } from 'primereact/toast';
import { DOC_MIME, DOCX_MIME, PDF_MIME } from '../../../common/mimeTypes';
import EmailService from '../../../services/emailService';
import { IApplyFormValidationResult } from './interfaces/IApplyFormValidationResult';
import { AppDispatch, RootState } from '../../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoadingOverlay, showLoadingOverlay } from '../../../state/slices/loadingOverlaySlice';
import AWSService from '../../../services/AWSService';

const ApplyFormComponent = ({ elementRef, jobTitle }: IApplyFormProps): JSX.Element => {
    const phrases = useSelector((state: RootState) => state.phrases.value);

    const MAX_ALLOWED_FILE_SIZE: number = 5_000_000; // 5 000 000 bytes = 5 MB.
    const ALLOWED_FILE_DESCRIPTION: string = `${phrases[104]}: PDF, DOC, DOCX. ${phrases[105]}: 5 MB.`;
    
    const acceptedMimeTypes = {
        doc: DOC_MIME,
        docx: DOCX_MIME,
        pdf: PDF_MIME,
    }
    const fileValidationRules = {
        fileFormat: phrases[106],
        fileSize: phrases[107],
    }

    const dispatch: AppDispatch = useDispatch<AppDispatch>();
    const githubInputRef = useRef<any>();
    const linkedInInputRef = useRef<any>();
    const fileUploadRef = useRef<any>();
    const toast = useRef<any>();
    const allowedFileDescriptionRef = useRef<any>();
    const formElementRef = elementRef;
    const defaultFormState: IApplyFormState = {
        gitHubProfile: "", //https://www.github.com/DDianovMD
        linkedInProfile: "", //https://www.linkedin.com/in/mariyanmarinov
        message: "",
        CVDownloadURL: ""
    }
    const [formState, setFormState] = useState<IApplyFormState>(defaultFormState);

    function gitHubProfileInputChangeHandler(event: any): void {
        setFormState((previous: IApplyFormState) => {
            return {
                ...previous, 
                gitHubProfile: event.target.value as string
            };
        });
    }

    function linkedInProfileInputChangeHandler(event: any): void {
        setFormState((previous: IApplyFormState) => {
            return {
                ...previous, 
                linkedInProfile: event.target.value as string
            };
        })
    }

    function messageInputChangeHandler(event: any): void {
        setFormState((previous: IApplyFormState) => {
            return {...previous, message: event.target.value as string};
        });
    }

    function invalidFileUploadedHandler(failedValidationRule: string): void {
        // Clear file input.
        fileUploadRef.current.value = '';
        // Reset description next to upload button.
        allowedFileDescriptionRef.current.textContent = ALLOWED_FILE_DESCRIPTION;

        //#region Invalid file notification
        if (failedValidationRule === fileValidationRules.fileSize) {
            toast.current.show({ 
                severity: 'error', 
                summary: phrases[108], 
                detail: phrases[109]
            });
        } else if (failedValidationRule === fileValidationRules.fileFormat) {
            toast.current.show({ 
                severity: 'error', 
                summary: phrases[110], 
                detail: phrases[111]
            });
        }
        //#endregion
    }

    function fileUploadHandler(event: any): void {
        const uploadedFile: File = event.target.files[0];
        
        // Check if file format is allowed.
        const isFileTypeAllowed = uploadedFile.type === acceptedMimeTypes.doc ||
                                uploadedFile.type === acceptedMimeTypes.docx ||
                                uploadedFile.type === acceptedMimeTypes.pdf;

        if(isFileTypeAllowed) {
            // Check if file size is not bigger than 5 MB.
            const isFileTooLarge = uploadedFile.size > MAX_ALLOWED_FILE_SIZE;
            if (isFileTooLarge) {
                invalidFileUploadedHandler(fileValidationRules.fileSize);
            } else {
                const showFileSizeInKB: boolean = uploadedFile.size < 1_000_000; // Check if file is less than 1 MB.
                const fileSize: number = showFileSizeInKB ? uploadedFile.size / 1_000 : uploadedFile.size / 1_000_000;
                
                // Update description text next to upload button.
                allowedFileDescriptionRef.current.textContent = `${uploadedFile.name}, ${fileSize.toFixed(2)} ${showFileSizeInKB ? 'KB' : 'MB'}.`;
            }
        } else {
            invalidFileUploadedHandler(fileValidationRules.fileFormat);
        }
    }

    function manageInputStateCSS(isValid: boolean, inputRef: any): void {
        if (!isValid) {
            inputRef.current.classList.add("invalid-input");
        } else {
            inputRef.current.classList.remove("invalid-input");
        }
    }

    function validateForm(): IApplyFormValidationResult {
        const formValidationResult: IApplyFormValidationResult = {
            isGitHubInputValid: FormValidationService.validateGitHubProfileInput(githubInputRef.current.value),
            isLinkedInInputValid: FormValidationService.validateLinkedInProfileInput(linkedInInputRef.current.value),
            isCVUploaded: fileUploadRef.current.value !== "",
        }
        return formValidationResult;
    }

    async function formSubmitHandler(event: any): Promise<void> {
        event.preventDefault();
        const formValidationResult: IApplyFormValidationResult = validateForm(); 
        const isFormValid: boolean = formValidationResult.isGitHubInputValid && formValidationResult.isLinkedInInputValid && formValidationResult.isCVUploaded;
        
        if (isFormValid) {
            dispatch(showLoadingOverlay(phrases[112]));
            const downloadUrl: string = await AWSService.uploadFile(fileUploadRef.current.files[0], toast.current);
            
            setFormState((previous: IApplyFormState) => {
                return {
                    ...previous,
                    CVDownloadURL: downloadUrl
                }
            });
            // Note: Logic for sending email is contained in useEffect hook which is executed after CVDownloadURL state is updated.
        } else {
            manageInputStateCSS(formValidationResult.isGitHubInputValid, githubInputRef);
            manageInputStateCSS(formValidationResult.isLinkedInInputValid, linkedInInputRef);
            if (!formValidationResult.isCVUploaded) {
                toast.current.show({ 
                    severity: 'error', 
                    summary: phrases[113], 
                    detail: phrases[114]
                });
            }
        }
    }

    // /**
    //  * Transforms file to base64 encoded string.
    //  * @param {File} file Uploaded file
    //  * @returns {Promise<string>} File as base64 encoded string.
    //  */
    // function EncodeAsBase64String(file: File): Promise<string> {
    //     return new Promise<string>((resolve, reject) => {
    //         const reader = new FileReader();
    //         reader.readAsDataURL(file);
    //         reader.onload = () => resolve(reader.result as string);
    //         reader.onerror = reject;
    //     });
    // }
    
    function openFileUpload(): void {
        fileUploadRef.current.click();
    }

    async function sendApplicationNotificationEmail(): Promise<boolean> {
        const response = await EmailService.sendEmail(formElementRef.current, true);
        return response.status === 200
    }

    /**
     * Callback that sends email after file is uploaded to AWS S3 successfully.
     */
    useEffect(() => {
        const shouldSendApplicationNotificationEmail: boolean = formState.CVDownloadURL !== "";

        if (shouldSendApplicationNotificationEmail) {
            sendApplicationNotificationEmail()
            .then((isEmailSent) => {
                // Always reset form and hide antispam overlay.
                fileUploadRef.current.value = '';

                setFormState((previous: IApplyFormState) => {
                    return defaultFormState;
                });
                
                dispatch(hideLoadingOverlay());

                // Notify user
                if (isEmailSent) {
                    toast.current.show({ 
                        severity: 'success', 
                        summary: phrases[115], 
                        detail: phrases[116],
                        life: 120000 // 2 minutes in miliseconds. Primereact does not have setting for toast to stay until user close it. 
                    });
                } else {
                    toast.current.show({ 
                        severity: 'error', 
                        summary: phrases[117], 
                        detail: phrases[118],
                        life: 120000 // 2 minutes in miliseconds. Primereact does not have setting for toast to stay until user close it.
                    });
                }
            });
        } else {
            // Hook is triggered by resetting the form after email is sent successfully. 
            // Reset the text next to upload button aswell.
            allowedFileDescriptionRef.current.textContent = ALLOWED_FILE_DESCRIPTION;
        }

    }, [formState.CVDownloadURL]);

    /**
     * Adjusts label's text for mobile screens.
     * @returns {void} Void.
     */
    useEffect((): void => {
        const isMobileScreenSize = window.innerWidth < 768;

        if (isMobileScreenSize) {
            const labelElements: NodeListOf<HTMLLabelElement> = document.querySelectorAll('.apply-form > div > label') as NodeListOf<HTMLLabelElement>;
            labelElements.forEach((label: HTMLLabelElement) => {
                // Remove ":" at the end of the label when it's positioned over the input and not beside it.
                label.textContent = label.textContent!.replace(':', '');
            });
        }
    }, []);

    return <>
        <form ref={formElementRef} encType="multipart/form-data" method="post" onSubmit={formSubmitHandler} className='flex-column apply-form collapsed'>
            <input type="hidden" name="position_name" value={jobTitle} />
            <input type="hidden" name="file_downloadUrl" value={formState.CVDownloadURL} />
            <div>
                <label htmlFor='github_profile' className='github-profile-label required'>{phrases[119]}</label>
                <InputText
                    type="text"
                    name="github_profile"
                    placeholder='https://www.github.com/your-username'
                    autoComplete='off'
                    className='p-inputtext-sm mb-15'
                    value={formState.gitHubProfile}
                    onChange={(event: any) => gitHubProfileInputChangeHandler(event)}
                    ref={githubInputRef}
                />
            </div>
            <div>
                <label htmlFor='linkedin_profile' className='linkedin_profile-label required'>{phrases[120]}</label>
                <InputText 
                    type="text"
                    name="linkedin_profile"
                    placeholder="https://www.linkedin.com/in/your-username/"
                    autoComplete="off"
                    className='p-inputtext-sm mb-15'
                    value={formState.linkedInProfile}
                    onChange={(event: any) => linkedInProfileInputChangeHandler(event)}
                    ref={linkedInInputRef}
                />
            </div>
            <div id="motivational-letter-container" className='d-flex flex-column'>
                <label htmlFor='message' className='d-block motivation-letter-label'>{phrases[121]}</label>
                <InputTextarea
                    name="message"
                    autoResize
                    rows={5}
                    cols={45}
                    value={formState.message}
                    onChange={(event: any) => messageInputChangeHandler(event)}
                />
            </div>
            <input 
                type='file'
                accept={`${acceptedMimeTypes.doc},${acceptedMimeTypes.docx},${acceptedMimeTypes.pdf}`}
                className='file-upload'
                ref={fileUploadRef}
                onChange={fileUploadHandler}
            />
            <div id="file-upload-container" className='mb-15'>
                <Button 
                    type='button'
                    icon="pi pi-file-arrow-up"
                    label={phrases[122]}
                    size="small"
                    rounded
                    onClick={openFileUpload}
                    className='file-upload-btn'
                />
                <span ref={allowedFileDescriptionRef} className='file-description'>{ALLOWED_FILE_DESCRIPTION}</span>
            </div>
            <div className='apply-btn-container'>
                <Button type="submit" label={phrases[123]} unstyled={false} size="small" className='contact-us__send-button' />
            </div>
        </form>
        <Toast ref={toast} />
    </>
}

export default ApplyFormComponent
